import React, { ReactNode } from "react";
import { Spinner } from "react-bootstrap";
import styled, { css } from "styled-components";

import Button from "src/components/Button";
import { useIsMobile } from "src/hooks/useIsMobile";
import { colors } from "src/styles";

type TableFilterField = {
  key: string;
  label: string;
  placeholder?: string;
};

export interface IDataTable {
  className?: string;
  headers?: string[];
  columnWidths?: string[]; // NEW
  disableHeader?: boolean;
  onRowClick?: (id: string) => void;
  clickable?: boolean;
  isLoading?: boolean;
  filterFields?: TableFilterField[];
  filterValues?: Record<string, string>;
  showFilters?: boolean;
  onToggleFilters?: () => void;
  onFilterChange?: (key: string, value: string) => void;
  onClearFilters?: () => void;
  onExport?: () => void;
  exportDisabled?: boolean;
  children: ReactNode;
}

const DataTable = ({
  className,
  headers,
  columnWidths,
  disableHeader,
  isLoading,
  filterFields,
  filterValues,
  showFilters,
  onToggleFilters,
  onFilterChange,
  onClearFilters,
  onExport,
  exportDisabled,
  children,
}: IDataTable) => {
  const { isMobile } = useIsMobile();
  const hasAnyFilterValue = !!filterFields?.some((field) => (filterValues?.[field.key] || "").trim().length > 0);

  const renderColGroup = () => {
    if (!columnWidths?.length) return null;
    return (
      <colgroup>
        {columnWidths.map((width, index) => (
          <col key={`col-${index}`} style={{ width }} />
        ))}
      </colgroup>
    );
  };

  const renderTableHeaders = () => {
    return headers?.map((header) => {
      return (
        <TableHeaderCell $isMobile={isMobile} disableHeader={disableHeader} key={header}>
          <span style={header === "Resource" ? { paddingLeft: 15 } : {}}>{header}</span>
        </TableHeaderCell>
      );
    });
  };

  const renderFilterHeaderRow = () => {
    if (!showFilters || !headers?.length) {
      return null;
    }

    return (
      <TableFilterRow>
        {headers.map((header, index) => {
          const filterField = filterFields?.[index] || filterFields?.find((field) => field.label === header);

          return (
            <TableFilterCell key={`${header}-filter`}>
              {filterField ? (
                <FilterInput
                  value={filterValues?.[filterField.key] || ""}
                  placeholder={filterField.placeholder || `Filter ${header.toLowerCase()}`}
                  onChange={(e) => onFilterChange?.(filterField.key, e.target.value)}
                />
              ) : null}
            </TableFilterCell>
          );
        })}
      </TableFilterRow>
    );
  };

  return (
    <Root className={className}>
      {isLoading ? (
        <Container>
          <Spinner animation="border" size="sm" />
        </Container>
      ) : (
        <>
          {!!onToggleFilters && !isMobile && (
            <TableControls>
              {!!onExport && (
                <Button text="Export" variant="small-secondary" onClick={onExport} disabled={exportDisabled} />
              )}
              <Button text="Filter" variant="small-secondary" onClick={onToggleFilters} />
              {!!onClearFilters && showFilters && (
                <Button text="Clear" variant="text" onClick={onClearFilters} disabled={!hasAnyFilterValue} />
              )}
            </TableControls>
          )}

          <TableViewport>
            <Table disableHeader={disableHeader}>
              {renderColGroup()}
              <TBody>
                <TableHeader disableHeader={disableHeader}>{renderTableHeaders()}</TableHeader>
                {renderFilterHeaderRow()}
                {children}
              </TBody>
            </Table>
          </TableViewport>
        </>
      )}
    </Root>
  );
};

export default DataTable;

export interface TableRowProps {
  clickable?: boolean;
}

export interface TableProps {
  disableHeader?: boolean;
}

export interface TableHeaderProps {
  disableHeader?: boolean;
  $isMobile?: boolean;
}

const Root = styled.div``;

const Table = styled.table<TableProps>`
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  background: ${colors.white.default};

  ${(props) =>
    props.disableHeader &&
    `margin-top: -60px; @media (max-width: 1560px) {
    margin-top: -60px;
  }`}
`;

const TableViewport = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 15px;
  min-height: 50vh;
`;

const TableHeaderCell = styled.th<TableHeaderProps>`
  padding: 12px 16px;
  font-style: normal;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${colors.grey.dark6};
  background: ${colors.white.default};
  border-bottom: 1px solid ${colors.grey.light3};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  text-align: left;

  ${(props) =>
    props.disableHeader &&
    `margin: 0;
  opacity: 0;
  height: 0;
  position: relative;
  z-index: -100;`}

  ${({ $isMobile }: { $isMobile?: boolean }) =>
    $isMobile &&
    css`
      padding: 8px;
    `}
`;

const TableHeader = styled.tr<TableHeaderProps>`
  background-color: ${colors.white.default};
  width: 100%;

  ${(props: TableHeaderProps) => props.disableHeader && `background: none`}
`;

const TBody = styled.tbody`
  width: 100%;
`;

const TableControls = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin: 0 0 12px;
`;

const TableFilterRow = styled.tr`
  background: ${colors.grey.light6};
`;

const TableFilterCell = styled.th`
  padding: 8px 12px 10px;
  border-bottom: 1px solid ${colors.grey.light3};
  background: ${colors.grey.light6};
`;

const FilterInput = styled.input`
  width: 100%;
  border: 1px solid ${colors.grey.light4};
  background: ${colors.white.default};
  color: ${colors.black.light2};
  font-size: 12px;
  padding: 7px 8px;
  outline: none;

  &:focus {
    border-color: ${colors.grey.dark3};
  }
`;
